import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useStore } from "zustand";
import { useApp } from "@/app/providers/app-provider";
import type { BonusTimeStatus } from "@/lib/application/AppManager";

const REWARDS_QUERY_KEY = "REWARDS";
const HAS_UNSEEN_QUERY_KEY = "REWARDS_HAS_UNSEEN";

export function useGetRewards() {
	const { appManager, appStore } = useApp();
	const accessToken = appStore.session?.access_token;

	return useQuery({
		queryKey: [REWARDS_QUERY_KEY],
		queryFn: () => appManager.getRewards(),
		enabled: !!accessToken,
	});
}

export function useMarkSeen() {
	const { appManager } = useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (rewardIds: string[]) => appManager.markRewardsSeen(rewardIds),
		onSuccess: () => {
			queryClient.setQueryData([HAS_UNSEEN_QUERY_KEY], {
				hasUnseenRewards: false,
			});
		},
	});
}

export function useBonusTimeStatus(): BonusTimeStatus {
	const { appManager } = useApp();
	const timer = useStore(
		appManager.useTimerStore,
		appManager.dailyLimitTimerSelector,
	);
	return appManager.getBonusTimeStatus(timer);
}

export function useHasUnseenRewards() {
	const { appManager, appStore, isSubscribed } = useApp();
	const accessToken = appStore.session?.access_token;

	return useQuery({
		queryKey: [HAS_UNSEEN_QUERY_KEY],
		queryFn: () => appManager.hasUnseenRewards(),
		enabled: !!accessToken && !isSubscribed,
		staleTime: 1000 * 60 * 5, // 5 minutes
	});
}
